import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

// PDF Export
export const exportToPDF = (data, columns, title = "Report", fileName = "report") => {
  const doc = new jsPDF();
  
  // Title
  doc.setFontSize(18);
  doc.text(title, 14, 22);
  
  // Date
  doc.setFontSize(10);
  doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 30);
  
  // Table setup
  const startY = 40;
  const cellPadding = 5;
  const cellHeight = 10;
  const pageWidth = doc.internal.pageSize.width;
  const marginLeft = 14;
  const marginRight = 14;
  const tableWidth = pageWidth - marginLeft - marginRight;
  const colWidth = tableWidth / columns.length;
  
  // Header
  doc.setFillColor(26, 54, 93); // #1a365d
  doc.rect(marginLeft, startY, tableWidth, cellHeight, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  
  columns.forEach((col, i) => {
    doc.text(col.label, marginLeft + (i * colWidth) + cellPadding, startY + 7);
  });
  
  // Data rows
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(9);
  
  let currentY = startY + cellHeight;
  
  data.forEach((row, rowIndex) => {
    // Check if we need a new page
    if (currentY > doc.internal.pageSize.height - 20) {
      doc.addPage();
      currentY = 20;
    }
    
    // Alternate row colors
    if (rowIndex % 2 === 0) {
      doc.setFillColor(248, 250, 252); // Light gray
      doc.rect(marginLeft, currentY, tableWidth, cellHeight, "F");
    }
    
    columns.forEach((col, i) => {
      const value = row[col.key] ?? "";
      const displayValue = col.format ? col.format(value) : String(value);
      doc.text(displayValue.substring(0, 20), marginLeft + (i * colWidth) + cellPadding, currentY + 7);
    });
    
    currentY += cellHeight;
  });
  
  // Footer
  doc.setFontSize(8);
  doc.setTextColor(128, 128, 128);
  doc.text(`Page 1 - Bahr Al Rimal Medical Distribution`, marginLeft, doc.internal.pageSize.height - 10);
  
  doc.save(`${fileName}-${Date.now()}.pdf`);
};

// Excel Export
export const exportToExcel = (data, columns, sheetName = "Data", fileName = "export") => {
  // Transform data with column labels
  const exportData = data.map((row) => {
    const newRow = {};
    columns.forEach((col) => {
      const value = row[col.key];
      newRow[col.label] = col.format ? col.format(value) : value;
    });
    return newRow;
  });
  
  // Create workbook
  const ws = XLSX.utils.json_to_sheet(exportData);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  
  // Generate buffer
  const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const blob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8" });
  
  saveAs(blob, `${fileName}-${Date.now()}.xlsx`);
};

// Sales Report specific export
export const exportSalesReport = (salesData, summary, format = "pdf") => {
  const columns = [
    { key: "date", label: "Date" },
    { key: "orders", label: "Orders" },
    { key: "revenue", label: "Revenue (LYD)", format: (v) => `${(v || 0).toFixed(2)} LYD` },
  ];
  
  if (format === "pdf") {
    const doc = new jsPDF();
    
    // Header
    doc.setFontSize(20);
    doc.setTextColor(26, 54, 93);
    doc.text("Sales Report", 14, 20);
    
    doc.setFontSize(12);
    doc.setTextColor(100, 100, 100);
    doc.text("Bahr Al Rimal Medical Distribution", 14, 28);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 35);
    
    // Summary
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text("Summary", 14, 50);
    
    doc.setFontSize(10);
    doc.text(`Total Revenue: ${(summary.total_revenue || 0).toFixed(2)} LYD`, 14, 60);
    doc.text(`Total Orders: ${summary.total_orders || 0}`, 14, 67);
    doc.text(`Profit: ${(summary.profit || 0).toFixed(2)} LYD`, 14, 74);
    doc.text(`Average Order Value: ${(summary.average_order_value || 0).toFixed(2)} LYD`, 14, 81);
    
    // Daily Sales Table
    if (salesData && salesData.length > 0) {
      doc.setFontSize(14);
      doc.text("Daily Sales", 14, 95);
      
      const startY = 102;
      const cellHeight = 8;
      const colWidths = [50, 40, 50];
      let currentY = startY;
      
      // Header
      doc.setFillColor(26, 54, 93);
      doc.rect(14, currentY, 140, cellHeight, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(9);
      doc.text("Date", 18, currentY + 6);
      doc.text("Orders", 68, currentY + 6);
      doc.text("Revenue", 108, currentY + 6);
      
      currentY += cellHeight;
      doc.setTextColor(0, 0, 0);
      
      salesData.forEach((row, i) => {
        if (currentY > 270) {
          doc.addPage();
          currentY = 20;
        }
        
        if (i % 2 === 0) {
          doc.setFillColor(248, 250, 252);
          doc.rect(14, currentY, 140, cellHeight, "F");
        }
        
        doc.text(row.date || "", 18, currentY + 6);
        doc.text(String(row.orders || 0), 68, currentY + 6);
        doc.text(`${(row.revenue || 0).toFixed(2)} LYD`, 108, currentY + 6);
        
        currentY += cellHeight;
      });
    }
    
    doc.save(`sales-report-${Date.now()}.pdf`);
  } else {
    exportToExcel(salesData, columns, "Sales Report", "sales-report");
  }
};

// Products Export
export const exportProducts = (products, format = "pdf") => {
  const columns = [
    { key: "name", label: "Product Name" },
    { key: "sku", label: "SKU" },
    { key: "brand", label: "Brand" },
    { key: "sale_price", label: "Price (LYD)", format: (v) => `${(v || 0).toFixed(2)}` },
    { key: "stock_quantity", label: "Stock" },
    { key: "category_name", label: "Category" },
  ];
  
  if (format === "pdf") {
    exportToPDF(products, columns, "Products Report", "products-report");
  } else {
    exportToExcel(products, columns, "Products", "products-export");
  }
};

// Orders Export
export const exportOrders = (orders, format = "pdf") => {
  const columns = [
    { key: "id", label: "Order ID", format: (v) => (v || "").slice(0, 8) },
    { key: "customer_name", label: "Customer" },
    { key: "status", label: "Status" },
    { key: "total", label: "Total (LYD)", format: (v) => `${(v || 0).toFixed(2)}` },
    { key: "created_at", label: "Date", format: (v) => v ? new Date(v).toLocaleDateString() : "" },
  ];
  
  if (format === "pdf") {
    exportToPDF(orders, columns, "Orders Report", "orders-report");
  } else {
    exportToExcel(orders, columns, "Orders", "orders-export");
  }
};

// Inventory Export
export const exportInventory = (products, format = "pdf") => {
  const columns = [
    { key: "name", label: "Product" },
    { key: "sku", label: "SKU" },
    { key: "stock_quantity", label: "Current Stock" },
    { key: "min_stock_alert", label: "Min Alert" },
    { key: "expiry_date", label: "Expiry Date" },
    { key: "batch_number", label: "Batch" },
  ];
  
  if (format === "pdf") {
    exportToPDF(products, columns, "Inventory Report", "inventory-report");
  } else {
    exportToExcel(products, columns, "Inventory", "inventory-export");
  }
};
